import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Plus, Save, ShieldAlert, Trash2 } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { SettingsUpdatedAt } from './SettingsUpdatedAt'

const MAX_CUSTOM_RULES = 25

const textInput =
  'h-9 px-2.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed'
const monoInput = textInput + ' font-mono'
const selectInput =
  'h-9 pl-2.5 pr-8 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'

/** Client-side regex syntax check for instant feedback. The server does the
 *  authoritative validation (including the ReDoS guard) on save. */
function regexError(pattern, caseInsensitive) {
  if (!pattern) return null
  try {
    void new RegExp(pattern, caseInsensitive ? 'i' : '')
    return null
  } catch (e) {
    return `Invalid pattern — ${e.message}`
  }
}

/** Detector rows, in display order, with operator-facing copy. Keys match
 *  the backend policy (`ai-firewall.engine.js`). */
const DETECTOR_META = [
  {
    key: 'prompt_injection',
    label: 'Prompt injection',
    help: 'Jailbreak and "ignore previous instructions" phrasing.',
  },
  {
    key: 'secret',
    label: 'Sensitive data (secrets)',
    help: 'API keys, private keys, credentials, SSNs and card numbers. Plain emails and phone numbers are deliberately ignored.',
  },
  {
    key: 'sql_dump',
    label: 'Database export',
    help: 'Dump tools (mysqldump / pg_dump), DROP TABLE and stacked-query patterns.',
  },
]

const CATEGORY_LABEL = {
  prompt_injection: 'Prompt injection',
  secret: 'Sensitive data',
  sql_dump: 'Database export',
}

const FEATURE_LABEL = {
  sop_tasks: 'SOP generation',
  report_rollup: 'AI report',
  workspace_template: 'Workspace template',
}

/** Per-feature policy rows, in display order. Keys match the backend
 *  `FEATURES` list in ai-firewall.engine.js. */
const FEATURE_META = [
  { key: 'sop_tasks', label: 'SOP generation' },
  { key: 'report_rollup', label: 'AI reports' },
  { key: 'workspace_template', label: 'Workspace template' },
]

/** Mirror of the backend `defaultPolicy()` — the shape the draft/snapshot
 *  always carry, so a cold render before the query resolves is still safe. */
const defaultDraft = {
  enabled: false,
  thresholds: { flag: 30, block: 70 },
  multiFindingBonus: 20,
  detectors: {
    prompt_injection: { enabled: true, weight: 40 },
    secret: { enabled: true, weight: 80 },
    sql_dump: { enabled: true, weight: 90 },
  },
  customRules: [],
  features: {},
}

function clampScore(value) {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, n))
}

/** Canonical, fixed-order tuple for a stable dirty-check. */
function canonical(p) {
  return [
    p.enabled,
    p.thresholds.flag,
    p.thresholds.block,
    p.multiFindingBonus,
    DETECTOR_META.map((d) => [p.detectors[d.key].enabled, p.detectors[d.key].weight]),
    (p.customRules ?? []).map((r) => [
      r.label,
      r.pattern,
      r.caseInsensitive,
      r.weight,
      r.enabled,
    ]),
    FEATURE_META.map((f) => {
      const fp = p.features?.[f.key] ?? {}
      return [
        fp.mode,
        fp.thresholds?.flag,
        fp.thresholds?.block,
        DETECTOR_META.map((d) => fp.detectors?.[d.key]),
      ]
    }),
  ]
}

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors cursor-pointer disabled:cursor-not-allowed ' +
        // A disabled switch keeps its KNOB POSITION — the stored value is
        // still what it is and takes effect the moment the master toggle
        // comes back on, so rendering it in the OFF position would be a
        // different lie — but it loses the green.
        //
        // QA (2026-09-01): with "Enable AI Firewall" off, the three detector
        // switches still read as active green next to the line "AI requests
        // are not screened". Colour is what a switch uses to say "live";
        // `disabled:opacity-50` alone was not enough, because a half-opacity
        // green is still unmistakably green.
        (disabled
          ? 'bg-[color:var(--color-rule-strong)] opacity-60'
          : checked
            ? 'bg-[color:var(--color-success)]'
            : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        aria-hidden="true"
        className={
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-5' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

const numberInput =
  'w-20 h-9 px-2.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed'

function ScoreInput({ value, onChange, disabled, ariaLabel }) {
  return (
    <input
      type="number"
      min={0}
      max={100}
      inputMode="numeric"
      aria-label={ariaLabel}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(clampScore(e.target.value))}
      onKeyDown={(e) => {
        // Whole numbers only — block the sign / exponent / decimal keys.
        if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault()
      }}
      className={numberInput}
    />
  )
}

export function AiFirewallSettings() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState(defaultDraft)
  const [serverSnapshot, setServerSnapshot] = useState(defaultDraft)

  const cfgQuery = useQuery({
    queryKey: ['platform-ai-firewall'],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/ai-firewall')
      return res.data
    },
  })

  const logQuery = useQuery({
    queryKey: ['platform-ai-firewall', 'log'],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/ai-firewall/log')
      return res.data?.data ?? { rows: [], total: 0, blocked: 0 }
    },
  })

  useEffect(() => {
    const data = cfgQuery.data?.data
    if (!data) return
    const next = {
      enabled: Boolean(data.enabled),
      thresholds: {
        flag: clampScore(data.thresholds?.flag ?? defaultDraft.thresholds.flag),
        block: clampScore(
          data.thresholds?.block ?? defaultDraft.thresholds.block,
        ),
      },
      multiFindingBonus: clampScore(
        data.multiFindingBonus ?? defaultDraft.multiFindingBonus,
      ),
      detectors: DETECTOR_META.reduce((acc, d) => {
        const src = data.detectors?.[d.key] ?? defaultDraft.detectors[d.key]
        acc[d.key] = {
          enabled: Boolean(src.enabled),
          weight: clampScore(src.weight),
        }
        return acc
      }, {}),
      customRules: Array.isArray(data.customRules)
        ? data.customRules.map((r) => ({
            id: r.id ?? crypto.randomUUID(),
            label: r.label ?? '',
            pattern: r.pattern ?? '',
            caseInsensitive: Boolean(r.caseInsensitive),
            weight: clampScore(r.weight ?? 50),
            enabled: r.enabled !== false,
          }))
        : [],
      features: FEATURE_META.reduce((acc, f) => {
        const src = data.features?.[f.key] ?? {}
        acc[f.key] = {
          mode: ['off', 'custom'].includes(src.mode) ? src.mode : 'inherit',
          thresholds: {
            flag: clampScore(src.thresholds?.flag ?? data.thresholds?.flag ?? 30),
            block: clampScore(
              src.thresholds?.block ?? data.thresholds?.block ?? 70,
            ),
          },
          detectors: DETECTOR_META.reduce((dacc, d) => {
            dacc[d.key] =
              src.detectors?.[d.key] !== undefined
                ? Boolean(src.detectors[d.key])
                : (data.detectors?.[d.key]?.enabled ?? true)
            return dacc
          }, {}),
        }
        return acc
      }, {}),
    }
    setDraft(next)
    setServerSnapshot(next)
  }, [cfgQuery.data])

  const isDirty = useMemo(
    () => JSON.stringify(canonical(draft)) !== JSON.stringify(canonical(serverSnapshot)),
    [draft, serverSnapshot],
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.put('/super-admin/platform/ai-firewall', draft)
      return res.data
    },
    onSuccess: () => {
      toast.success('AI Firewall settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['platform-ai-firewall'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function patchThreshold(key, value) {
    setDraft((prev) => ({
      ...prev,
      thresholds: { ...prev.thresholds, [key]: value },
    }))
  }
  function patchDetector(key, patch) {
    setDraft((prev) => ({
      ...prev,
      detectors: {
        ...prev.detectors,
        [key]: { ...prev.detectors[key], ...patch },
      },
    }))
  }
  function addRule() {
    setDraft((prev) => ({
      ...prev,
      customRules: [
        ...prev.customRules,
        {
          id: crypto.randomUUID(),
          label: '',
          pattern: '',
          caseInsensitive: true,
          weight: 50,
          enabled: true,
        },
      ],
    }))
  }
  function patchRule(id, patch) {
    setDraft((prev) => ({
      ...prev,
      customRules: prev.customRules.map((r) =>
        r.id === id ? { ...r, ...patch } : r,
      ),
    }))
  }
  function removeRule(id) {
    setDraft((prev) => ({
      ...prev,
      customRules: prev.customRules.filter((r) => r.id !== id),
    }))
  }
  function patchFeature(key, patch) {
    setDraft((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: { ...prev.features[key], ...patch } },
    }))
  }
  function patchFeatureThreshold(key, tkey, value) {
    setDraft((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: {
          ...prev.features[key],
          thresholds: { ...prev.features[key].thresholds, [tkey]: value },
        },
      },
    }))
  }
  function patchFeatureDetector(key, dkey, enabled) {
    setDraft((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: {
          ...prev.features[key],
          detectors: { ...prev.features[key].detectors, [dkey]: enabled },
        },
      },
    }))
  }

  const log = logQuery.data ?? { rows: [], total: 0, blocked: 0 }

  return (
    <section>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <ShieldAlert
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            AI Firewall
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Screen the content of every AI request, platform-wide. Each detector
            adds to a risk score that decides whether a request is allowed,
            flagged, or blocked.
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save size={14} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {cfgQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : cfgQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(cfgQuery.error).message}
        </p>
      ) : (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          {/* Master toggle */}
          <div className="px-6 py-5 border-b border-[color:var(--color-rule)] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
                Enable AI Firewall
              </h3>
              <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                {draft.enabled
                  ? 'Every AI request is screened per the rules below.'
                  : 'AI requests are not screened.'}
              </p>
            </div>
            <Toggle
              checked={draft.enabled}
              onChange={(v) => setDraft((prev) => ({ ...prev, enabled: v }))}
              label="Enable AI Firewall"
            />
          </div>

          {/* Risk thresholds */}
          <div className="px-6 py-5 border-b border-[color:var(--color-rule)]">
            <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
              Risk thresholds
            </h3>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              A request scores the weight of each detector it trips. Below the
              flag score it is allowed; at or above the flag score it is logged;
              at or above the block score it is refused.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-4">
              <label className="flex items-center gap-2.5">
                <span className="text-sm text-[color:var(--color-ink)]">
                  Flag At
                </span>
                <ScoreInput
                  value={draft.thresholds.flag}
                  onChange={(v) => patchThreshold('flag', v)}
                  disabled={!draft.enabled}
                  ariaLabel="Flag score threshold"
                />
              </label>
              <label className="flex items-center gap-2.5">
                <span className="text-sm text-[color:var(--color-ink)]">
                  Block at
                </span>
                <ScoreInput
                  value={draft.thresholds.block}
                  onChange={(v) => patchThreshold('block', v)}
                  disabled={!draft.enabled}
                  ariaLabel="Block score threshold"
                />
              </label>
              <label className="flex items-center gap-2.5">
                <span className="text-sm text-[color:var(--color-ink)]">
                  Multiple-category bonus
                </span>
                <ScoreInput
                  value={draft.multiFindingBonus}
                  onChange={(v) =>
                    setDraft((prev) => ({ ...prev, multiFindingBonus: v }))
                  }
                  disabled={!draft.enabled}
                  ariaLabel="Multiple-category bonus"
                />
              </label>
            </div>
            {draft.thresholds.block < draft.thresholds.flag ? (
              <p className="mt-3 text-xs text-[color:var(--color-priority-urgent)]">
                The block score is below the flag score — it will be raised to
                match the flag score on save.
              </p>
            ) : null}
          </div>

          {/* Detectors */}
          <div className="px-6 py-5">
            <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
              Detectors
            </h3>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              {draft.enabled
                ? 'Turn each detector on or off and set how much it adds to the score.'
                : 'Enable the AI Firewall above to change these. Your selections are kept and apply as soon as it is switched on.'}
            </p>
            <div className="mt-4 divide-y divide-[color:var(--color-rule)]">
              {DETECTOR_META.map((d) => {
                const det = draft.detectors[d.key]
                return (
                  <div
                    key={d.key}
                    className="py-3 flex items-start justify-between gap-4"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Toggle
                        checked={det.enabled}
                        onChange={(v) => patchDetector(d.key, { enabled: v })}
                        disabled={!draft.enabled}
                        label={d.label}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
                          {d.label}
                        </p>
                        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                          {d.help}
                        </p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-[color:var(--color-ink-muted)]">
                        Weight
                      </span>
                      <ScoreInput
                        value={det.weight}
                        onChange={(v) => patchDetector(d.key, { weight: v })}
                        disabled={!draft.enabled || !det.enabled}
                        ariaLabel={`${d.label} weight`}
                      />
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Custom rules */}
      {!cfgQuery.isLoading && !cfgQuery.isError ? (
        <div className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
                Custom rules
              </h3>
              <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                Add your own regular-expression detectors. Each rule adds its
                weight to the score when it matches.
              </p>
            </div>
            <button
              type="button"
              onClick={addRule}
              disabled={
                !draft.enabled || draft.customRules.length >= MAX_CUSTOM_RULES
              }
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              Add rule
            </button>
          </div>
          <div className="px-6 py-5 space-y-3">
            {draft.customRules.length === 0 ? (
              <p className="text-sm text-[color:var(--color-ink-muted)]">
                No custom rules yet.
              </p>
            ) : (
              draft.customRules.map((rule) => {
                const err = regexError(rule.pattern, rule.caseInsensitive)
                return (
                  <div
                    key={rule.id}
                    className="rounded-lg border border-[color:var(--color-rule)] p-3"
                  >
                    <div className="flex items-center gap-3">
                      <Toggle
                        checked={rule.enabled}
                        onChange={(v) => patchRule(rule.id, { enabled: v })}
                        disabled={!draft.enabled}
                        label={`Enable ${rule.label || 'rule'}`}
                      />
                      <input
                        type="text"
                        value={rule.label}
                        placeholder="Rule name"
                        maxLength={60}
                        disabled={!draft.enabled}
                        onChange={(e) =>
                          patchRule(rule.id, { label: e.target.value })
                        }
                        className={textInput + ' flex-1 min-w-0'}
                        aria-label="Rule name"
                      />
                      <label className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs text-[color:var(--color-ink-muted)]">
                          Weight
                        </span>
                        <ScoreInput
                          value={rule.weight}
                          onChange={(v) => patchRule(rule.id, { weight: v })}
                          disabled={!draft.enabled || !rule.enabled}
                          ariaLabel="Rule weight"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeRule(rule.id)}
                        disabled={!draft.enabled}
                        aria-label="Delete rule"
                        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-danger-bg)] hover:text-[color:var(--color-danger)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <input
                        type="text"
                        value={rule.pattern}
                        placeholder="Regular expression, e.g. PROJECT-\d{4}"
                        maxLength={200}
                        spellCheck={false}
                        disabled={!draft.enabled}
                        onChange={(e) =>
                          patchRule(rule.id, { pattern: e.target.value })
                        }
                        className={
                          monoInput +
                          ' flex-1 min-w-0' +
                          (err
                            ? ' border-[color:var(--color-danger)] focus:ring-[color:var(--color-danger)]'
                            : '')
                        }
                        aria-label="Rule pattern"
                      />
                      <label className="flex items-center gap-1.5 shrink-0 text-xs text-[color:var(--color-ink-muted)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rule.caseInsensitive}
                          disabled={!draft.enabled}
                          onChange={(e) =>
                            patchRule(rule.id, {
                              caseInsensitive: e.target.checked,
                            })
                          }
                        />
                        Ignore case
                      </label>
                    </div>
                    {err ? (
                      <p className="mt-1.5 text-xs text-[color:var(--color-danger)]">
                        {err}
                      </p>
                    ) : null}
                  </div>
                )
              })
            )}
            <p className="text-xs text-[color:var(--color-ink-subtle)]">
              Up to {MAX_CUSTOM_RULES} rules. Patterns are validated on save;
              unsafe patterns (which could hang the server) are rejected.
            </p>
          </div>
        </div>
      ) : null}

      {/* Per-feature policies */}
      {!cfgQuery.isLoading && !cfgQuery.isError ? (
        <div className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[color:var(--color-rule)]">
            <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
              Per-feature policies
            </h3>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              Override the global settings for a specific AI feature — turn the
              firewall off for one, or give it its own thresholds and detectors.
            </p>
          </div>
          <div className="divide-y divide-[color:var(--color-rule)]">
            {FEATURE_META.map((f) => {
              const fp = draft.features[f.key] ?? {
                mode: 'inherit',
                thresholds: { flag: 30, block: 70 },
                detectors: {},
              }
              return (
                <div key={f.key} className="px-6 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                      {f.label}
                    </span>
                    <select
                      value={fp.mode}
                      disabled={!draft.enabled}
                      onChange={(e) => patchFeature(f.key, { mode: e.target.value })}
                      className={selectInput}
                      aria-label={`${f.label} policy`}
                    >
                      <option value="inherit">Inherit global</option>
                      <option value="off">Off</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  {fp.mode === 'custom' ? (
                    <div className="mt-3 space-y-3">
                      <div className="flex flex-wrap gap-x-6 gap-y-3">
                        <label className="flex items-center gap-2">
                          <span className="text-sm text-[color:var(--color-ink)]">
                            Flag At
                          </span>
                          <ScoreInput
                            value={fp.thresholds.flag}
                            onChange={(v) => patchFeatureThreshold(f.key, 'flag', v)}
                            disabled={!draft.enabled}
                            ariaLabel={`${f.label} flag threshold`}
                          />
                        </label>
                        <label className="flex items-center gap-2">
                          <span className="text-sm text-[color:var(--color-ink)]">
                            Block at
                          </span>
                          <ScoreInput
                            value={fp.thresholds.block}
                            onChange={(v) => patchFeatureThreshold(f.key, 'block', v)}
                            disabled={!draft.enabled}
                            ariaLabel={`${f.label} block threshold`}
                          />
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {DETECTOR_META.map((d) => (
                          <label
                            key={d.key}
                            className="flex items-center gap-1.5 text-sm text-[color:var(--color-ink)] cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(fp.detectors[d.key])}
                              disabled={!draft.enabled}
                              onChange={(e) =>
                                patchFeatureDetector(f.key, d.key, e.target.checked)
                              }
                            />
                            {CATEGORY_LABEL[d.key]}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-[color:var(--color-ink-muted)]">
                        Detector weights and custom rules stay from the global
                        settings.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                      {fp.mode === 'off'
                        ? 'The firewall does not screen this feature.'
                        : 'Uses the global settings above.'}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {/* Recent activity */}
      <div className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] m-0">
            Recent activity
          </h3>
          {log.total > 0 ? (
            <span className="text-xs text-[color:var(--color-ink-muted)]">
              {log.blocked} blocked / {log.total} total
            </span>
          ) : null}
        </div>
        {logQuery.isLoading ? (
          <p className="px-6 py-5 text-sm text-[color:var(--color-ink-muted)]">
            Loading…
          </p>
        ) : log.rows.length === 0 ? (
          <p className="px-6 py-5 text-sm text-[color:var(--color-ink-muted)]">
            No firewall activity yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[color:var(--color-ink-subtle)] border-b border-[color:var(--color-rule)]">
                  <th className="px-6 py-2.5 font-medium">When</th>
                  <th className="px-4 py-2.5 font-medium">Company</th>
                  <th className="px-4 py-2.5 font-medium">Feature</th>
                  <th className="px-4 py-2.5 font-medium">Detected</th>
                  <th className="px-4 py-2.5 font-medium text-right">Score</th>
                  <th className="px-6 py-2.5 font-medium text-right">Result</th>
                </tr>
              </thead>
              <tbody>
                {log.rows.map((r) => {
                  const categories = [
                    ...new Set((r.findings ?? []).map((f) => f.category)),
                  ]
                    .map((c) => CATEGORY_LABEL[c] ?? c)
                    .join(', ')
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-[color:var(--color-rule)] last:border-0"
                    >
                      <td className="px-6 py-2.5 whitespace-nowrap text-[color:var(--color-ink-muted)]">
                        {r.createdAt ? formatDateTime(r.createdAt) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[color:var(--color-ink)]">
                        {r.tenantName ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[color:var(--color-ink)]">
                        {FEATURE_LABEL[r.feature] ?? r.feature ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[color:var(--color-ink)]">
                        {categories || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[color:var(--color-ink)]">
                        {r.score}
                      </td>
                      <td className="px-6 py-2.5 text-right">
                        <span
                          className={
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ' +
                            (r.blocked
                              ? 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)]'
                              : 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)]')
                          }
                        >
                          {r.blocked ? 'Blocked' : 'Flagged'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SettingsUpdatedAt at={cfgQuery.data?.updatedAt} />
    </section>
  )
}
